/**
 * The 14-day jamaat grid (work hub, 2026-10-31): the panel's read-only preview made editable, and the app's per-day list.
 *
 * Rows are days, columns the prayers (Jumu'ah in Dhuhr's place on a Friday). A cell is what every reader sees — the
 * day resolved through jamaat-rules.resolve(): the override on top, then the mosque's own period, then IRN's, then the
 * year table — with where it came from, so the panel can say «endret» on a cell an override set and offer «Angre».
 * Editing a cell is a per-day override (prayer_override, the existing override API): nothing here writes; this module
 * lays the grid out, checks a typed time before it is sent (the server checks again, core overrides.validate), parses a
 * pasted column, and says where Tab/Enter go. Pure: no clock, no database.
 */
'use strict';
const JR = require('./jamaat-rules');
const overrides = require('./overrides');

const PRAYERS = JR.PRAYERS;
const COLS = [...PRAYERS];
/** The override column a cell writes to. */
const PATCH_KEY = { fajr: 'fajr_jamaat', dhuhr: 'dhuhr_jamaat', asr: 'asr_jamaat', maghrib: 'maghrib_jamaat', isha: 'isha_jamaat', jumua: 'jumua_jamaat' };
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const OVERRIDE_FIELDS = ['fajr_jamaat', 'dhuhr_jamaat', 'asr_jamaat', 'maghrib_jamaat', 'isha_jamaat', 'jumua_khutbah', 'jumua_jamaat'];

/**
 * grid(days, periods, overrides) → [{ date, isFriday, isClosed, missing, cells: { fajr: cell, … } }] where a cell is
 * { prayer, key (the override column), begins, jamaat, source ('override' | 'staff' | 'irn' | 'timetable' | null),
 * overridden (an override set it), flag }. `days` = prayer_day rows in date order (one past the end gives Isha its
 * window); `periods` = jamaat_period rows; `overrides` = prayer_override rows (is_closed as 0/1 or boolean). On a
 * Friday the Dhuhr column carries Jumu'ah (prayer 'jumua', key jumua_jamaat), as the app and the screens show it.
 */
function grid(days, periods, ovs, { count = 14, from = null } = {}) {
  const rows = (days || []).slice().sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const byDate = new Map(rows.map((d) => [d.date, d]));
  const ovByDate = new Map((ovs || []).map((o) => [o.date, o]));
  const start = from || (rows[0] && rows[0].date);
  if (!start) return [];
  const out = [];
  for (let i = 0; i < count; i++) {
    const date = JR.addDays(start, i);
    const d = byDate.get(date);
    if (!d) { out.push({ date, isFriday: JR.isFriday(date), isClosed: false, missing: true, cells: {} }); continue; }
    const ov = ovByDate.get(date) || null;
    const r = JR.resolve(d, ov ? { ...ov, is_closed: Boolean(ov.is_closed) } : null, periods || [], byDate.get(JR.addDays(date, 1)) || null);
    const friday = JR.isFriday(date);
    const cells = {};
    for (const p of PRAYERS) {
      const prayer = p === 'dhuhr' && friday ? 'jumua' : p;
      const field = prayer === 'jumua' ? 'jumua_jamaat' : p;
      const value = prayer === 'jumua' ? (r.resolved.jamaat && r.resolved.jamaat.jumuaJamaat) || (r.resolved.jamaat && r.resolved.jamaat.dhuhr) || null
        : (r.resolved.jamaat && r.resolved.jamaat[p]) || null;
      const source = prayer === 'jumua' ? (r.sources.jumua_jamaat || r.sources.dhuhr || null) : (r.sources[p] || null);
      const flag = (r.flags || []).find((f) => f.field === field || (prayer === 'jumua' && f.field === 'dhuhr')) || null;
      cells[p] = { prayer, key: PATCH_KEY[prayer], begins: (r.resolved.begins && r.resolved.begins[p]) || null, jamaat: value,
        source: value ? source : null, overridden: Boolean(ov && ov[PATCH_KEY[prayer]]), flag: flag ? { code: flag.code, value: flag.value } : null };
    }
    out.push({ date, isFriday: friday, isClosed: Boolean(r.resolved.isClosed), missing: false, cells,
      // The day's begins as a timetable row, so a client can check a typed time with cellInput() before it is sent.
      day: { date, sunrise: d.sunrise || null, ...Object.fromEntries(PRAYERS.map((p) => [`${p}_begins`, d[`${p}_begins`] || null])) },
      override: ov ? { reason: ov.reason_no || null, isClosed: Boolean(ov.is_closed), fields: OVERRIDE_FIELDS.filter((k) => ov[k] != null && ov[k] !== '') } : null });
  }
  return out;
}

/**
 * One typed value for a cell → { ok, value, patch } | { ok: false, code, message }. Accepts «7:15», «07.15», «0715»;
 * empty means «clear this cell's override» (patch value null). With `day` (the prayer_day row, and `nextDay`) the time
 * is held to its prayer: not before it begins, not after its time is out — the same two rules the server applies
 * (overrides.jamaatAfterBegins / jamaatInWindow), so a refusal is said before anything is sent.
 */
function cellInput(day, prayer, raw, nextDay = null) {
  const key = PATCH_KEY[prayer];
  if (!key) return { ok: false, code: 'bad_prayer', message: 'Ukjent bønn.' };
  const s = String(raw == null ? '' : raw).trim();
  if (s === '') return { ok: true, value: null, patch: { [key]: null } };
  const m = /^(\d{1,2})[:.]?(\d{2})$/.exec(s);
  if (!m || Number(m[1]) > 23 || Number(m[2]) > 59) return { ok: false, code: 'bad_value', message: 'Skriv et klokkeslett som TT:MM.' };
  const value = `${String(Number(m[1])).padStart(2, '0')}:${m[2]}`;
  if (day) {
    const p = prayer === 'jumua' ? 'dhuhr' : prayer;
    const early = overrides.jamaatAfterBegins({ [`${p}_jamaat`]: value }, day);
    if (early.length) return { ok: false, code: 'before_begins', message: early[0].message };
    const late = overrides.jamaatInWindow({ [`${p}_jamaat`]: value }, day, nextDay);
    if (late.length) return { ok: false, code: 'after_window', message: late[0].message };
  }
  return { ok: true, value, patch: { [key]: value } };
}

/** A pasted column: times separated by newlines (or semicolons) → ['07:15', '07:20', …]; a blank line is null (skip). */
function pasteColumn(text) {
  return String(text || '').split(/\r?\n|;/).map((l) => l.trim()).filter((l, i, all) => l !== '' || i < all.length - 1)
    .map((l) => { if (l === '') return null; const m = /^(\d{1,2})[:.]?(\d{2})$/.exec(l); return m && Number(m[1]) <= 23 && Number(m[2]) <= 59 ? `${String(Number(m[1])).padStart(2, '0')}:${m[2]}` : null; });
}

/**
 * Where a key goes from (row, col) in a grid of `rows` rows and COLS columns, like a spreadsheet: Tab → the next cell
 * (wrapping to the next row), Shift+Tab back, Enter → the same column one row down, Shift+Enter one row up. Null: stay.
 */
function move(row, col, key, rows, { shift = false } = {}) {
  const n = COLS.length;
  if (key === 'Tab') {
    const i = row * n + col + (shift ? -1 : 1);
    if (i < 0 || i >= rows * n) return null;
    return { row: Math.floor(i / n), col: i % n };
  }
  if (key === 'Enter') {
    const r = row + (shift ? -1 : 1);
    return r < 0 || r >= rows ? null : { row: r, col };
  }
  return null;
}

/** The whole override for a day after one cell changes: the cells the override still sets, or none (then the row goes). */
function remaining(override, key) {
  const left = OVERRIDE_FIELDS.filter((k) => k !== key && override && override[k] != null && override[k] !== '');
  return { fields: left, empty: left.length === 0 && !(override && override.is_closed) };
}

module.exports = { PRAYERS, COLS, PATCH_KEY, OVERRIDE_FIELDS, grid, cellInput, pasteColumn, move, remaining };
